// @flow
import * as React from 'react';
import { forwardRef } from 'react';
import classNames from 'classnames';
import {
  getMatchFromIconClassList,
  accessibilityText,
} from 'javascripts/utils/link-type';
import Icon from 'components/_particles/icon/react/Icon';
import { t } from 'javascripts/utils/withTranslation';

export type IconLinkType = {
  tag?: string,
  children: string,
  text?: string,
  link?: string,
  square?: boolean,
  stacked?: boolean,
  emphasis?: boolean,
  modest?: boolean,
  type?: 'button' | 'submit',
  className?: string,
  icon: string,
  attr?: Object,
  download?: boolean,
  size?: string,
  large?: boolean,
  onClick?: (event: SyntheticEvent<HTMLButtonElement>) => any,
  right?: boolean,
  target?: string,
  overlayTarget?: string,
  external?: boolean,
  id?: string,
};

export const accessibilityLinkText = (
  identifier: string,
  external: boolean,
  children: React.Node
): React$MixedElement => {
  const type = getMatchFromIconClassList([identifier]);
  const addAccessabilityText = accessibilityText?.[type];
  const addTargetText = external ? ` (${t('Öffnet in neuem Fenster')})` : '';
  const $span = () =>
    addAccessabilityText ? (
      <span className="u-hidden-visually">{addAccessabilityText}</span>
    ) : null;
  const $targetSpan = () =>
    addTargetText ? (
      <span className="u-hidden-visually">{addTargetText}</span>
    ) : null;

  return (
    <>
      <$span />
      {children}
      <$targetSpan />
    </>
  );
};

const IconLink: React.ForwardRefRenderFunction<IconLinkType, HTMLElement> =
  forwardRef((props: IconLinkType, ref) => {
    const {
      link,
      children,
      square,
      stacked,
      className,
      type,
      icon = 'arrow-right-line',
      onClick,
      attr = {},
      emphasis,
      modest,
      download,
      tag,
      size,
      large,
      right,
      target,
      overlayTarget,
      external,
      id,
    } = props;
    const Tag = (link && 'a') || tag || 'button';

    return (
      <Tag
        className={classNames(
          'icon-link',
          {
            'icon-link--large': size === 'large',
            'icon-link--social': size === 'social',
            'icon-link--right': right,
            'js-overlay-link': overlayTarget,
          },
          className
        )}
        href={link}
        type={type || (!link && !tag ? 'button' : undefined)}
        rel={external && 'noopener noreferrer'}
        target={(external || download) && '_blank'}
        download={download}
        id={id}
        data-overlay-target={overlayTarget}
        ref={ref}
        onClick={onClick}
        {...attr}
      >
        <span className="icon-link__button">
          <Icon icon={icon} className="icon-link__icon" />
        </span>
        <span className="icon-link__text">
          {accessibilityLinkText(icon, external || false, children)}
        </span>
      </Tag>
    );
  });

IconLink.displayName = 'IconLink';
export default IconLink;
